import {Alert, Box, Button, Typography} from '@mui/material';
import {useState, type ReactNode} from 'react';
import type {DocumentUpload, FormField, FormSection, ResolvedWidget, StepDetail, StepGlobal, StepUpload} from '../dtos';
import type {PaymentUpdateTerms} from '../dtos/payment-plan-update';
import {downloadUpload} from '../api';
import {useOptionSources} from '../hooks/use-option-sources.hook';
import {evaluatePreconditionState, preconditionSupported} from '../utils/conditions';
import {detach} from '../utils/detach';
import {draftGates, draftValues} from '../utils/payment-update-fields';
import {fieldRuleErrors} from '../utils/validate';
import FormFieldInput from './form-fields';
import StepWidget from './finance-widgets';

type FieldMappings = Record<'cashPrice' | 'downPayment' | 'tenorYears' | 'frequency' | 'specialInstallmentsCount', string> & {loanAmount?: string; propertyUsage?: string};
const fieldKey = (section: FormSection, field: FormField): string => field.k ?? field.ref ?? `${section.name}.${field.l}`;

/** The calculator placement previews the PROPOSED plan, so it reads the draft terms, not the saved ones. */
function widgetPreview(widget: ResolvedWidget, terms: PaymentUpdateTerms, mappings?: FieldMappings): ResolvedWidget {
  if (widget.widget !== 'paymentPlanCalculator') return widget;
  // The saved Finance plan belongs to the current application; the revision preview owns its proposed terms.
  const previewWidget = {...widget};
  delete previewWidget.interestRate;
  const inputKeys = mappings
    ? {inputKeys: {cashPrice: mappings.cashPrice, downPayment: mappings.downPayment, tenorYears: mappings.tenorYears, frequency: mappings.frequency, leadingMonths: mappings.specialInstallmentsCount, ...(mappings.propertyUsage ? {propertyUsage: mappings.propertyUsage} : {})}}
    : {};
  return {...previewWidget, inputs: {...terms}, parameters: {...widget.parameters, ...inputKeys}};
}

function SubmittedUpload({upload, onDownloadError}: Readonly<{upload: StepUpload | DocumentUpload; onDownloadError: (message: string) => void}>) {
  const download = (): void => {
    onDownloadError('');
    detach(downloadUpload(upload.uid, upload.fileName).catch(() => onDownloadError('Unable to download the submitted document.')));
  };
  return <Box>
    <Button onClick={download}>{upload.fileName}</Button>
    <Typography variant="caption">{upload.decision ?? 'Uploaded'}{upload.rejectionReason ? ` — ${upload.rejectionReason}` : ''}</Typography>
  </Box>;
}

interface GlobalFieldProps {
  step: StepDetail;
  field: FormField;
  global?: StepGlobal;
  terms: PaymentUpdateTerms;
  mappings?: FieldMappings;
  values: Record<string, unknown>;
  validationError?: string;
  showIncomeDocuments: boolean;
  incomeDocuments: ReactNode;
  onDownloadError: (message: string) => void;
}
/** A `ref` placement: a widget preview, the documents already submitted against it, or a plain value. */
function GlobalField({step, field, global, terms, mappings, values, validationError, showIncomeDocuments, incomeDocuments, onDownloadError}: Readonly<GlobalFieldProps>) {
  const widget = global?.widget;
  if (widget) {
    const liveFacts = widget.widget === 'dbrCalculator' ? undefined : values;
    return <Box sx={{gridColumn: '1 / -1'}}>
      <StepWidget key={JSON.stringify(widget.dbrInputs ?? {})} widget={widgetPreview(widget, terms, mappings)} liveFacts={liveFacts} stepUid={step.uid} disabled draftPreview draftValidationError={validationError} />
    </Box>;
  }
  const uploads = global?.document?.uploads ?? (step.uploads ?? []).filter(upload => upload.fieldKey === field.ref);
  return <Box>
    <Typography variant="subtitle2">{global?.label ?? field.l ?? field.ref}</Typography>
    {uploads.map(upload => <SubmittedUpload key={upload.uid} upload={upload} onDownloadError={onDownloadError} />)}
    {global?.type === 'document' && <Typography variant="caption">Current submitted document</Typography>}
    {!uploads.length && global?.value != null && <Typography>{String(global.value)}</Typography>}
    {showIncomeDocuments && incomeDocuments}
  </Box>;
}

interface FieldSlotProps extends Omit<GlobalFieldProps, 'global' | 'showIncomeDocuments'> {
  globals: Map<string, StepGlobal>;
  fieldValueKey: string;
  incomeKeys: Set<string>;
  fieldError?: string;
  parentValue: unknown;
  hiddenOptions: string[];
  inputDisabled: boolean;
  onChange: (value: unknown) => void;
}
/** One grid cell: the step's own input, or a global placement the step's rules may have revived. */
function FieldSlot(props: Readonly<FieldSlotProps>) {
  const {step, field, globals, values, fieldValueKey, incomeKeys, fieldError, parentValue, hiddenOptions, inputDisabled, onChange} = props;
  if (!field.ref) {
    return <FormFieldInput field={field} value={values[fieldValueKey]} onChange={onChange} disabled={inputDisabled}
      error={fieldError} hiddenOptions={hiddenOptions} parentValue={parentValue} />;
  }
  const global = globals.get(field.ref);
  const revived = step.preconditionFacts !== undefined && preconditionSupported(field.precondition) && evaluatePreconditionState(field.precondition, values) === 'visible';
  if (global?.active === false && !revived) return null;
  return <GlobalField {...props} global={global} showIncomeDocuments={incomeKeys.has(field.ref)} />;
}

export interface PaymentPlanUpdateFormProps {
  step: StepDetail;
  terms: PaymentUpdateTerms;
  formData: Record<string, unknown>;
  editableFieldKeys: string[];
  disabled: boolean;
  canEditPropertyUsage?: boolean;
  validationError?: string;
  onTermsChange: (patch: Partial<PaymentUpdateTerms>) => void;
  onFieldChange: (key: string, value: unknown) => void;
  incomeDocuments: ReactNode;
}

export function PaymentPlanUpdateForm({step, terms, formData, editableFieldKeys, disabled, canEditPropertyUsage, validationError, onTermsChange, onFieldChange, incomeDocuments}: Readonly<PaymentPlanUpdateFormProps>) {
  const sources = useOptionSources().data?.sources;
  const [downloadError, setDownloadError] = useState('');
  const schema = step.formSchema ?? [];
  const config = step.uiFlags?.paymentPlanUpdate as {fieldKeys?: FieldMappings; incomeDocumentFields?: {fieldKey: string}[]} | undefined;
  const mappings = config?.fieldKeys;
  const termsByKey: Record<string, keyof PaymentUpdateTerms> = mappings ? {[mappings.cashPrice]: 'cashPrice', [mappings.downPayment]: 'downPayment', [mappings.tenorYears]: 'tenorYears', [mappings.frequency]: 'frequency', [mappings.specialInstallmentsCount]: 'leadingMonths', ...(mappings.propertyUsage ? {[mappings.propertyUsage]: 'propertyUsage' as const} : {})} : {};
  const editable = new Set([...Object.keys(termsByKey), ...editableFieldKeys].filter(key => canEditPropertyUsage !== false || key !== mappings?.propertyUsage));
  const ownKeys = new Set(schema.flatMap(section => section.fields.filter(field => !field.ref && !field.readonly).map(field => fieldKey(section, field))));
  const prefilled = new Set(Object.keys(step.prefill ?? {}));
  const ambient = Object.fromEntries(Object.entries(step.preconditionFacts ?? {}).filter(([key]) => !ownKeys.has(key) || prefilled.has(key)));
  const values = draftValues({...step, preconditionFacts: ambient}, terms, formData);
  const gates = draftGates(step, values);
  const errors = fieldRuleErrors(schema, values);
  const bySource: Record<string, unknown> = {};
  for (const section of schema) for (const field of section.fields) if (field.optionsFrom) bySource[field.optionsFrom] = values[fieldKey(section, field)];
  const globals = new Map((step.globals ?? []).map(global => [global.ref, global]));
  const incomeKeys = new Set(config?.incomeDocumentFields?.map(item => item.fieldKey) ?? []);
  const change = (key: string, value: unknown): void => {
    if (disabled || !editable.has(key) || gates.hiddenFields.has(key) || gates.lockedFields.has(key)) return;
    const term = termsByKey[key];
    term ? onTermsChange({[term]: term === 'frequency' || term === 'propertyUsage' ? value : Number(value)}) : onFieldChange(key, value);
  };
  const parentValueFor = (field: FormField): unknown =>
    field.optionsFrom ? bySource[sources?.find(source => source.key === field.optionsFrom)?.dependsOn ?? ''] : undefined;
  const inputDisabledFor = (key: string, field: FormField): boolean =>
    disabled || !editable.has(key) || Boolean(field.readonly) || Boolean(field.derive && !field.deriveEditable && !termsByKey[key]) || gates.lockedFields.has(key);
  return <>{downloadError && <Alert severity="error">{downloadError}</Alert>}{schema.map(section => {
    if (gates.hiddenSections.has(section.name)) return null;
    return <Box key={section.name} sx={{mb: 2}}><Typography variant="h6" sx={{mb: 1}}>{section.name}</Typography><Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 2}}>
      {section.fields.map(field => {
        const key = fieldKey(section, field);
        if (gates.hiddenFields.has(key)) return null;
        return <FieldSlot key={key} step={step} field={field} fieldValueKey={key} globals={globals} terms={terms} mappings={mappings}
          values={values} validationError={validationError} incomeKeys={incomeKeys} incomeDocuments={incomeDocuments}
          fieldError={errors[key]} parentValue={parentValueFor(field)} hiddenOptions={[...(gates.hiddenOptions.get(key) ?? [])]}
          inputDisabled={inputDisabledFor(key, field)} onChange={value => change(key, value)} onDownloadError={setDownloadError} />;
      })}
    </Box></Box>;
  })}{!schema.some(section => section.fields.some(field => field.ref && incomeKeys.has(field.ref))) && incomeDocuments}</>;
}
